import {
  DateRangePicker,
  Select,
  ToggleGroup,
  ToggleGroupItem,
} from "@win-reality/react-ui";
import { LayoutGroup } from "framer-motion";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

import { HorizontalMenu } from "@/components/ui/HorizontalMenu";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Spinner } from "@/components/ui/spinner";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDateRange } from "@/hooks/useDateRange";
import { ROUTES } from "@/lib/routes";
import { usePlayerProfileStore } from "@/stores/player-profile";
import { splitName } from "@/utils/splitName";

import { usePlayerInfo } from "./hooks/usePlayerInfo";

export default function PlayerProfile() {
  const { id: playerId } = useParams() as { id: string };
  const location = useLocation();
  const { data, isLoading, error } = usePlayerInfo({
    playerId,
  });

  const {
    playerType,
    dateFrom,
    dateTo,
    gameType,
    vsHand,
    setPlayerType,
    setDateFrom,
    setDateTo,
    setGameType,
    setVsHand,
    setSelectedMachine,
    selectedMachine,
  } = usePlayerProfileStore(
    useShallow((state) => ({
      playerType: state.playerType,
      dateFrom: state.dateFrom,
      dateTo: state.dateTo,
      gameType: state.gameType,
      vsHand: state.vsHand,
      setPlayerType: state.setPlayerType,
      setDateFrom: state.setDateFrom,
      setDateTo: state.setDateTo,
      setGameType: state.setGameType,
      setVsHand: state.setVsHand,
      setSelectedMachine: state.setSelectedMachine,
      selectedMachine: state.selectedMachine,
    })),
  );

  const navigate = useNavigate();

  const [isHome, setIsHome] = useState(true);

  useEffect(() => {
    setIsHome(/^\/players\/[^/]+\/home$/.test(location.pathname));
  }, [location]);

  const isMobile = useIsMobile();

  const { dateRange, handleDateRangeChange } = useDateRange({
    initialFrom: dateFrom,
    initialTo: dateTo,
    onFromChange: setDateFrom,
    onToChange: setDateTo,
  });

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        data-testid="spinner-container"
      >
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (error) {
    navigate(ROUTES.NOT_FOUND, { replace: true });
    return null;
  }

  const { firstName, lastName } = splitName(data?.known_names?.[0] || "");
  const machinesPlayedAt = data?.machines_played_at ?? [];

  return (
    <SidebarProvider keyboardShortcut="k">
      <div className="flex flex-col w-full">
        <HorizontalMenu
          items={[
            {
              label: "Overview",
              active: isHome,
              onClick: () => navigate(`/players/${playerId}/home`),
            },
            {
              label: "Charts",
              active: !isHome,
              onClick: () => navigate(`/players/${playerId}/charts`),
            },
          ]}
        />
        <div className="flex flex-1">
          <Sidebar
            collapsible={isMobile ? undefined : "none"}
            className="bg-white"
          >
            <SidebarHeader className="flex flex-col items-center py-4 md:pt-16">
              <div className="flex flex-col gap-1 items-center">
                <div className="relative h-40 w-40 overflow-hidden rounded-full bg-gray-200 flex items-center justify-center">
                  {firstName?.trim() || lastName?.trim() ? (
                    <span className="text-4xl font-semibold text-gray-600">
                      {firstName?.trim()?.[0]?.toUpperCase()}
                      {lastName?.trim()?.[0]?.toUpperCase()}
                    </span>
                  ) : (
                    <img
                      src="/src/assets/default-user.svg"
                      alt="Default user avatar"
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <h2 className="text-lg font-semibold">
                  {`${firstName?.trim() || ""} ${lastName?.trim() || ""}`}
                </h2>

                {data?.city && (
                  <p className="text-sm text-gray-500">
                    {data?.city}
                    {data?.state && `, ${data?.state}`}
                  </p>
                )}

                {data?.current_team_name && (
                  <p className="text-sm text-gray-500">
                    {data?.current_team_name}
                  </p>
                )}

                {/* bats throws */}
                <p className="text-sm text-gray-500">
                  Throws: {data?.throws} | Bats: {data?.bats}
                </p>
              </div>
            </SidebarHeader>
            <SidebarContent>
              <div className="flex flex-col space-y-4 p-4">
                <>
                  <LayoutGroup id="player-type">
                    <ToggleGroup
                      type="single"
                      value={playerType}
                      onValueChange={(value: "hitter" | "pitcher") => {
                        if (value) setPlayerType(value);
                      }}
                      className="justify-center"
                    >
                      <ToggleGroupItem
                        value="hitter"
                        aria-label="Hitter player type"
                        className="flex-1 text-sm"
                      >
                        Hitter
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value="pitcher"
                        aria-label="Pitcher player type"
                        className="flex-1 text-sm"
                      >
                        Pitcher
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </LayoutGroup>

                  {!isHome && (
                    <LayoutGroup id="game-type">
                      <ToggleGroup
                        type="single"
                        value={gameType}
                        onValueChange={(value: "game" | "practice") => {
                          if (value) setGameType(value);
                        }}
                        className="justify-center"
                      >
                        <ToggleGroupItem
                          value="game"
                          aria-label="Game type"
                          className="flex-1 text-sm"
                        >
                          Games
                        </ToggleGroupItem>
                        <ToggleGroupItem
                          value="practice"
                          aria-label="Practice type"
                          className="flex-1 text-sm"
                        >
                          Practice
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </LayoutGroup>
                  )}

                  {machinesPlayedAt.length > 0 && !isHome && (
                    <div className="w-full">
                      <Select
                        items={machinesPlayedAt.map((machine) => ({
                          value: machine.machine_uuid,
                          label: machine.name,
                        }))}
                        value={selectedMachine?.machine_uuid || undefined}
                        onValueChange={(value) => {
                          if (value === selectedMachine?.machine_uuid) return;
                          const machine = machinesPlayedAt.find(
                            (m) => m.machine_uuid === value,
                          );
                          if (machine) setSelectedMachine(machine);
                        }}
                        placeholder="Select machine"
                      />
                    </div>
                  )}
                </>

                <p className="text-sm text-gray-500 mt-4">Date range</p>
                <DateRangePicker
                  range={dateRange}
                  onRangeChange={handleDateRangeChange}
                />

                {!isHome && (
                  <>
                    <p className="text-sm text-gray-500 mt-4">Vs handedness</p>
                    <LayoutGroup id="vs-hand">
                      <ToggleGroup
                        type="single"
                        value={vsHand}
                        onValueChange={(value: "all" | "righty" | "lefty") => {
                          if (value) setVsHand(value);
                        }}
                        className="justify-center"
                      >
                        <ToggleGroupItem
                          value="all"
                          aria-label="Against all handedness"
                          className="flex-1 text-sm"
                        >
                          All
                        </ToggleGroupItem>
                        <ToggleGroupItem
                          value="righty"
                          aria-label="Against right-handed opponents"
                          className="flex-1 text-sm"
                        >
                          Righty
                        </ToggleGroupItem>
                        <ToggleGroupItem
                          value="lefty"
                          aria-label="Against left-handed opponents"
                          className="flex-1 text-sm"
                        >
                          Lefty
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </LayoutGroup>
                  </>
                )}
              </div>
            </SidebarContent>
          </Sidebar>

          <SidebarInset className="flex-1">
            <div className="p-3 sm:p-6 h-full">
              <SidebarTrigger className="block md:hidden" />
              <Outlet />
            </div>
          </SidebarInset>
        </div>
      </div>
    </SidebarProvider>
  );
}
